import React, { createContext, useState, useCallback, useEffect, useContext, useMemo } from 'react';
import { DASHBOARD_VIEW, DASHBOARD_VIEW_STORAGE_KEY } from '../constants/dashboardViewMode';

function readStoredViewMode() {
  try {
    const v = localStorage.getItem(DASHBOARD_VIEW_STORAGE_KEY);
    if (v === DASHBOARD_VIEW.basic || v === DASHBOARD_VIEW.pro) {
      return v;
    }
  } catch (e) {
    // ignore
  }
  return DASHBOARD_VIEW.basic;
}

const UiViewModeContext = createContext(null);

export function UiViewModeProvider({ children }) {
  const [viewMode, setViewModeState] = useState(readStoredViewMode);

  const setViewMode = useCallback(
    (next) => {
      if (next !== DASHBOARD_VIEW.basic && next !== DASHBOARD_VIEW.pro) {
        return;
      }
      if (next === viewMode) {
        return;
      }
      try {
        localStorage.setItem(DASHBOARD_VIEW_STORAGE_KEY, next);
      } catch (e) {
        // ignore
      }
      // Full reload so trade/dashboard and other views re-mount cleanly in the new layout.
      window.location.reload();
    },
    [viewMode],
  );

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== DASHBOARD_VIEW_STORAGE_KEY) {
        return;
      }
      if (e.newValue === DASHBOARD_VIEW.basic || e.newValue === DASHBOARD_VIEW.pro) {
        setViewModeState(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({
      viewMode,
      setViewMode,
      isPro: viewMode === DASHBOARD_VIEW.pro,
    }),
    [viewMode, setViewMode],
  );

  return <UiViewModeContext.Provider value={value}>{children}</UiViewModeContext.Provider>;
}

export function useUiViewMode() {
  const ctx = useContext(UiViewModeContext);
  if (ctx == null) {
    throw new Error('useUiViewMode must be used under UiViewModeProvider');
  }
  return ctx;
}

export { UiViewModeContext };
